import React, { Fragment, useState, useEffect } from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Box from '@mui/material/Box';
import { Typography, Checkbox, Button, Avatar, Badge, colors } from '@leapeasy/ui-kit';

import { createClaimAction, setErrorAction } from 'store/actions/claimFormActions';
import ConfirmDialog from 'components/ClaimForm/ConfirmDialog';
import {
  isStagedUploadEnabled,
  getFileDisplayName,
  reviewDocumentCategorySx,
  reviewDocumentFileNameStyle,
} from 'utils/stagedUpload';
import { getLandlordDisplayName, getTenantDobValue } from 'components/ClaimForm/helpers';

const CLAIM_TYPE_LABELS = {
  damageOnly: 'Damages Only',
  noShow: 'No Show',
  tenantDefault: 'Resident Default',
  damages: 'Damages + Other',
  holdOver: 'Holdover',
};

const DAMAGE_TYPE_LABELS = {
  trash: 'Trash',
  scratches: 'Scratches/Holes/Burns',
  other: 'Other',
};

const PLACE_DAMAGE_LABELS = {
  application: 'Appliance Damages',
  carpert: 'Carpet/Flooring',
  wall: 'Wall/Ceilings',
  other: 'Other',
};

const renderDocumentCategory = (label, files) => {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!list.length) {
    return null;
  }

  const sectionLabel = list.length > 1 ? `${label}s (${list.length})` : label;

  return (
    <Box sx={reviewDocumentCategorySx}>
      <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
        {sectionLabel}
      </Typography>
      {list.map((file, index) => {
        const displayName = getFileDisplayName(file);
        return (
          <Typography
            key={file?.id || `${label}-${index}`}
            variant="cta-xs"
            component="div"
            style={{
              ...reviewDocumentFileNameStyle,
              color: colors.purple[900],
              marginTop: '8px',
              fontWeight: '500',
            }}
            title={displayName}
          >
            {displayName}
          </Typography>
        );
      })}
    </Box>
  );
};

const Form7 = (props) => {
  const { actions, formData, handleNext, handleBack, handleStep } = props;
  const [termCheck, setTermCheck] = useState(false);
  const [data, setFormData] = useState({
    // Step-1: Locate Your Apartment
    state: null,
    landlord: null,
    apartment: null,
    policy: null,
    // Resident information (policy occupants)
    tenants: [],

    // Type of Claim
    acceptableDocument: '',
    // Extra
    paymentAgreement: null,
    agreePayDate: null,
    depositAmount: null,
    vacantCheck: null,
    evictionCheck: null,
    keyCheck: null,
    rentDate: null,
    evictionDate: null,
    contactName: null,
    contactEmail: null,
    contactPhone: null,
    contactTitle: null,
    payee: null,
    receiptEmail: null,
    address1: null,
    address2: null,
    currentCity: null,
    currentState: null,
    currentZipCode: null,

    // secondary info
    paymentMethod: null,
    secondaryAdded: null,
    secondaryName: null,
    secondaryPhone: null,
    secondaryTitle: null,
    secondaryEmail: null,
    secondaryPaymentMail: null,
    secondaryPaymentAddress: null,
    secondaryPaymentCity: null,
    secondaryPaymentState: null,
    secondaryPaymentZipcode: null,
  });

  useEffect(() => {
    const landlordObj = formData.landlordArray?.filter(
      (item) => item.Landlord__c === formData.data.landlord,
    );
    const apartmentObj = formData.apartmentArray?.filter(
      (item) => item.Id === formData.data.apartment,
    );
    const policyObj = formData.policyArray?.filter((item) => item.Id === formData.data.policy);

    const landlord =
      formData.data.landlordName ||
      (landlordObj?.[0] ? getLandlordDisplayName(landlordObj[0]) : null);
    const apartment = formData.data.propertyName || apartmentObj?.[0]?.Name || null;
    const policy = formData.data.riderName || policyObj?.[0]?.Renewal_Rider_ID__c || null;

    setFormData({
      kindOfClaim: 'llp',
      state: formData.data.state,
      landlord,
      apartment,
      policy,
      tenants: formData.tenants,
      acceptableDocument: formData.data.acceptableDocument,
      leaseTermIscorrect: formData.data.isCorrectTerm,
      paymentAgreement: formData.data.paymentAgreement,
      agreePayDate: formData.data.agreePayDate,
      depositAmount: formData.data.depositAmount,
      vacantCheck: formData.data.vacantCheck,
      evictionCheck: formData.data.evictionCheck,
      keyCheck: formData.data.keyCheck,
      rentDate: formData.data.rentDate,
      evictionDate: formData.data.evictionDate,
      contactName: formData.data.contactName,
      contactEmail: formData.data.contactEmail,
      contactPhone: formData.data.contactPhone,
      contactTitle: formData.data.contactTitle,
      // address2: formData.data.address2,
      claimType: formData.data.claimType,
      relatedExpense: formData.data.relatedExpense,
      damageType: formData.data.damageType,
      placeDamages: formData.data.placeDamages,
      lateFiling: formData.data.lateFiling,
      lateFilingExplanation: formData.data.lateFilingExplanation,
      // secondary contact & payment info
      paymentMethod: formData.data.paymentMethod,
      // echeck
      receiptPayee: formData.data.receiptPayee,
      receiptEmail: formData.data.receiptEmail,
      // physical_check
      physicalName: formData.data.physicalName,
      physicalEmail: formData.data.physicalEmail,
      address1: formData.data.address1,
      currentCity: formData.data.currentCity,
      currentState: formData.data.currentState,
      currentZipCode: formData.data.currentZipCode,
      // ACH
      payee: formData.data.payee,
      payeeEmail: formData.data.payeeEmail,

      secondaryAdded: formData.data.secondaryAdded,
      secondaryName: formData.data.secondaryName,
      secondaryPhone: formData.data.secondaryPhone,
      secondaryTitle: formData.data.secondaryTitle,
      secondaryEmail: formData.data.secondaryEmail,

      additionalComment: formData.data.additional_comment,
      // secondaryPaymentMail: formData.data.secondaryPaymentMail,
      // secondaryPaymentAddress: formData.data.secondaryPaymentAddress,
      // secondaryPaymentCity: formData.data.secondaryPaymentCity,
      // secondaryPaymentState: formData.data.secondaryPaymentState,
      // secondaryPaymentZipcode: formData.data.secondaryPaymentZipcode,
    });
  }, [formData]);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    setSubmitting(true);

    const claimPayload = {
      kindOfClaim: 'llp',
      tenants: formData.tenants,
      propertyState: formData.data.state,
      securityDeposit: formData.data.depositAmount,
      contactName: formData.data.contactName,
      contactEmail: formData.data.contactEmail,
      contactTitle: formData.data.contactTitle,
      contactPhone: formData.data.contactPhone,
      rentDate: formData.data.rentDate,
      occupiedStatus: formData.data.vacantCheck,
      keysReturned: formData.data.keyCheck,
      evictionFiledStatus: formData.data.evictionCheck,
      evictionFilingDate: formData.data.evictionDate,
      agreedPaymentDate: formData.data.agreePayDate,
      paymentAgreement: formData.data.paymentAgreement,
      checkName: formData.data.payee,
      checkEmail: formData.data.receiptEmail,
      checkAddress1: formData.data.address1,
      checkAddress2: formData.data.checkAddress2,
      checkState: formData.data.currentState,
      checkCity: formData.data.currentCity,
      checkZipcode: formData.data.currentZipCode,
      riderId: formData.data.policy,
      leaseTermIscorrect: formData.data.isCorrectTerm,
      claimType: formData.data.claimType,
      relatedExpense: formData.data.relatedExpense,
      damageType: formData.data.damageType,
      placeDamages: formData.data.placeDamages,
      lateFilingExplanation: formData.data.lateFilingExplanation,
      paymentMethod: formData.data.paymentMethod,
      receiptPayee: formData.data.receiptPayee,
      receiptEmail: formData.data.receiptEmail,
      physicalName: formData.data.physicalName,
      physicalEmail: formData.data.physicalEmail,
      address1: formData.data.address1,
      currentCity: formData.data.currentCity,
      currentState: formData.data.currentState,
      currentZipCode: formData.data.currentZipCode,
      payee: formData.data.payee,
      payeeEmail: formData.data.payeeEmail,
      secondaryAdded: formData.data.secondaryAdded,
      secondaryName: formData.data.secondaryName,
      secondaryPhone: formData.data.secondaryPhone,
      secondaryTitle: formData.data.secondaryTitle,
      secondaryEmail: formData.data.secondaryEmail,
      additionalComment: formData.data.additional_comment,
      showCancellationDisclaimer: Boolean(formData.data.showCancellationDisclaimer),
      cancellationAcknowledgementDateTime:
        formData.data.cancellationAcknowledgementDateTime || null,
      cancellationDisclaimerAcknowledged: Boolean(formData.data.showCancellationDisclaimer),
      cancellationDisclaimerAcknowledgedAt: formData.data.showCancellationDisclaimer
        ? new Date().toISOString()
        : null,
    };

    if (!isStagedUploadEnabled()) {
      Object.assign(claimPayload, {
        ledgerFile: formData.data.ledgerFile[0],
        leaseFile: formData.data.leaseFile[0],
        otherFiles: formData.data.otherFiles,
      });
    } else {
      if (!formData.data.uploadSessionId) {
        actions.setErrorAction(
          'Document upload session is missing. Please go back and re-upload your documents.',
        );
        setOpen(false);
        setSubmitting(false);
        return;
      }
      claimPayload.uploadSessionId = formData.data.uploadSessionId;
    }

    try {
      actions.createClaimAction(claimPayload);
      setOpen(false);
      handleNext();
    } finally {
      setSubmitting(false);
    }
  };

  const cardStyle = {
    padding: '12px',
    background: 'rgba(249, 248, 249, 0.8)',
    border: '1px solid #F8F4F9',
    borderRadius: '12px',
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',
  };
  const contentStyle = {
    padding: '24px',
    border: '1px solid #F8F4F9',
    borderRadius: '12px',
    gap: '10px',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flexWrap: 'wrap',
  };
  const itemStyle = {
    width: '50%',
    flexGrow: 0,
    flexShrink: 0,
    borderRight: '1px solid #E8D6EB',
    boxSizing: 'border-box',
    paddingRight: '22px',
  };

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="20px">
        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="1" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Locate Your Apartment
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(0)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Property State
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.state}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Property Name
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.apartment}
                </Typography>
              </Box>
            </Box>

            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Operator
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.landlord}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Policy Number
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.policy}
                </Typography>
              </Box>
            </Box>

            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Lease Start Date
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {formData.data.leaseStartDate}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Lease End Date
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {formData.data.leaseEndDate}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="2" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Resident Confirmation
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(1)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Box>
              {data.tenants.map((tenant, index) => (
                <Box
                  key={index}
                  display={'flex'}
                  marginBottom={data.tenants.length - 1 === index ? '0px' : '20px'}
                >
                  <Box sx={itemStyle}>
                    <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                      Resident
                    </Typography>
                    <Typography
                      variant="cta-xs"
                      style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                    >
                      {tenant.FirstName + ' ' + tenant.LastName}
                    </Typography>
                  </Box>

                  <Box sx={{ paddingLeft: '22px' }}>
                    <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                      Birthday
                    </Typography>
                    <Typography
                      variant="cta-xs"
                      style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                    >
                      {getTenantDobValue(tenant) || '—'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="3" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Type of Claim
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(2)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Box>
              <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                Type of Claim
              </Typography>

              <Box display="flex" gap={1} flexWrap="wrap" marginTop="8px">
                {data.claimType &&
                  Object.entries(data.claimType)
                    .filter(([, selected]) => selected)
                    .map(([key]) => (
                      <Badge
                        key={key}
                        color="warmBlue"
                        label={CLAIM_TYPE_LABELS[key] || key}
                        textSize="small"
                        showClose={false}
                      />
                    ))}
              </Box>
            </Box>

            {data.damageType && Object.keys(data.damageType).length !== 0 && (
              <Box>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Type of Damages
                </Typography>

                <Box display="flex" gap={1} flexWrap="wrap" marginTop="8px">
                  {Object.entries(data.damageType)
                    .filter(([, selected]) => selected)
                    .map(([key]) => (
                      <Badge
                        key={key}
                        color="warmBlue"
                        label={DAMAGE_TYPE_LABELS[key] || key}
                        textSize="small"
                        showClose={false}
                      />
                    ))}
                </Box>
              </Box>
            )}

            {data.placeDamages && Object.keys(data.placeDamages).length !== 0 && (
              <Box>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Place of Damages
                </Typography>

                <Box display="flex" gap={1} flexWrap="wrap" marginTop="8px">
                  {Object.entries(data.placeDamages)
                    .filter(([, selected]) => selected)
                    .map(([key]) => (
                      <Badge
                        key={key}
                        color="magentaRed"
                        label={PLACE_DAMAGE_LABELS[key] || key}
                        textSize="small"
                        showClose={false}
                      />
                    ))}
                </Box>
              </Box>
            )}

            {data.claimType?.noShow && (
              <Box>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Acceptable Document
                </Typography>

                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data?.acceptableDocument}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                Security Deposit Amount on Hand
              </Typography>
              <Typography
                variant="cta-xs"
                style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
              >
                {data.depositAmount}
              </Typography>
            </Box>

            {(data.agreePayDate && (
              <Box display="flex">
                <Box sx={itemStyle}>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Payment Agreement Status
                  </Typography>

                  <Typography
                    variant="cta-xs"
                    style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                  >
                    {data.paymentAgreement}
                  </Typography>
                </Box>

                <Box sx={{ paddingLeft: '22px' }}>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Agreed Payment Date
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                  >
                    {data.agreePayDate}
                  </Typography>
                </Box>
              </Box>
            )) || (
              <Box>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Payment Agreement Status
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.paymentAgreement}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="4" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Claim Detail
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(3)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Date of Loss
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.rentDate}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Resident left?
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.vacantCheck}
                </Typography>
              </Box>
            </Box>

            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Keys returned?
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.vacantCheck === 'Yes' && data.keyCheck}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Filed for eviction?
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.evictionCheck}
                </Typography>
              </Box>
            </Box>

            {data.lateFiling && (
              <Box>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Explanation for the reason for late filing
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.lateFilingExplanation}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="5" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Contact Information
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(4)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Typography
              variant="cta-xs"
              style={{
                color: colors.purple[900],
                margin: '8px 0',
                fontWeight: '500',
                fontSize: '14px',
              }}
            >
              Primary Contact Information
            </Typography>
            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Contact Name
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.contactName}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Contact Title
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                >
                  {data.contactTitle}
                </Typography>
              </Box>
            </Box>

            <Box display="flex">
              <Box sx={itemStyle}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Contact Email
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.contactEmail}
                </Typography>
              </Box>

              <Box sx={{ paddingLeft: '22px' }}>
                <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                  Contact Phone Number
                </Typography>
                <Typography
                  variant="cta-xs"
                  style={{
                    color: colors.purple[900],
                    marginTop: '8px',
                    fontWeight: '500',
                    wordWrap: 'break-word',
                  }}
                >
                  {data.contactPhone}
                </Typography>
              </Box>
            </Box>
          </Box>

          {data.secondaryAdded && (
            <Box sx={contentStyle}>
              <Typography
                variant="cta-xs"
                style={{
                  color: colors.purple[900],
                  margin: '8px 0',
                  fontWeight: '500',
                  fontSize: '14px',
                }}
              >
                Secondary Contact Information
              </Typography>
              <Box display="flex">
                <Box sx={itemStyle}>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Contact Name
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                  >
                    {data.secondaryName}
                  </Typography>
                </Box>

                <Box sx={{ paddingLeft: '22px' }}>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Contact Title
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{ color: colors.purple[900], marginTop: '8px', fontWeight: '500' }}
                  >
                    {data.secondaryTitle}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex">
                <Box sx={itemStyle}>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Contact Email
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.secondaryEmail}
                  </Typography>
                </Box>

                <Box sx={{ paddingLeft: '22px' }}>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Contact Phone Number
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.secondaryPhone}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="6" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Payment Method
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(5)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Box>
              <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                Payment Method
              </Typography>
              <Typography
                variant="cta-xs"
                style={{
                  color: colors.purple[900],
                  marginTop: '8px',
                  fontWeight: '500',
                  wordWrap: 'break-word',
                }}
              >
                {data.paymentMethod}
              </Typography>
            </Box>
            {data.paymentMethod == 'echeck' ? (
              <React.Fragment>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Who does the check need to be issued to?
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.receiptPayee}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Check recipt email
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.receiptEmail}
                  </Typography>
                </Box>
              </React.Fragment>
            ) : (
              <></>
            )}
            {data.paymentMethod == 'physical_check' ? (
              <React.Fragment>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Who does the check need to be issued to?
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.physicalName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Check recipt email
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.physicalEmail}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Address Line 1
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.address1}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    City
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.currentCity}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    State
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.currentState}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Zip Code
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.currentZipCode}
                  </Typography>
                </Box>
              </React.Fragment>
            ) : (
              <></>
            )}
            {data.paymentMethod == 'ACH' ? (
              <React.Fragment>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Who does the check need to be issued to?
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.payee}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="cta-xs" style={{ color: '#A39AA9' }}>
                    Payment Reciept Email
                  </Typography>
                  <Typography
                    variant="cta-xs"
                    style={{
                      color: colors.purple[900],
                      marginTop: '8px',
                      fontWeight: '500',
                      wordWrap: 'break-word',
                    }}
                  >
                    {data.payeeEmail}
                  </Typography>
                </Box>
              </React.Fragment>
            ) : (
              <></>
            )}
          </Box>
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="7" state="none" bgColor="#E8D6EB" />

            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Document Upload
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(6)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            {renderDocumentCategory('Lease Agreement File', formData.data.leaseFile)}
            {renderDocumentCategory('Detailed Resident Ledger', formData.data.ledgerFile)}
            {renderDocumentCategory('Other File', formData.data.otherFiles)}
          </Box>
        </Box>

        <Box sx={cardStyle}>
          <Box display="flex" alignItems="center" gap="12px">
            <Avatar size="medium" iconLabel="8" state="none" bgColor="#E8D6EB" />
            <Typography variant="body1" style={{ fontWeight: '700' }}>
              Additional Comment
            </Typography>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="tertiary"
              size="small"
              iconPrefix="EditorLayout.Edit"
              onClick={() => handleStep(7)}
              style={{ width: 'fit-content' }}
            />
          </Box>

          <Box sx={contentStyle}>
            <Typography
              variant="cta-xs"
              style={{
                color: colors.purple[900],
                marginTop: '8px',
                fontWeight: '500',
                wordWrap: 'break-word',
              }}
            >
              {data.additionalComment}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box display="flex" gap="12px" alignItems="center" mt={2}>
        <Checkbox value={termCheck || false} onChange={(val) => setTermCheck(val)} />

        <Typography variant="small" style={{ color: colors.purple[900] }}>
          Agree with&nbsp;
          <a
            href="https://www.leapeasy.com/leap-lease-program-terms-conditions/"
            target="_blank"
            color="primary"
            style={{ color: colors.purple[700] }}
          >
            Terms & Conditions
          </a>
        </Typography>
      </Box>

      <Box
        sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', mt: 4, gap: '16px' }}
      >
        <Button size="medium" variant="secondary" onClick={handleBack} style={{ flexShrink: 2 }}>
          Back
        </Button>

        <Button
          size="medium"
          variant="primary"
          onClick={() => setOpen(true)}
          disabled={!termCheck || submitting}
          style={{ flexShrink: 1 }}
        >
          {submitting ? 'Submitting...' : 'OK'}
        </Button>
      </Box>

      <ConfirmDialog
        open={open}
        handleClose={() => setOpen(false)}
        handleOk={handleOk}
        showCancellationDisclaimer={Boolean(formData?.data?.showCancellationDisclaimer)}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    formData: state.getIn(['formData']),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    actions: bindActionCreators(
      {
        createClaimAction,
        setErrorAction,
      },
      dispatch,
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Form7);
